import format from 'date-fns/format';
import { PO_TRANSMIT_INACTIVE } from 'src/services/manage_suppliers/supplierFilterItemMappers';
import { TRANSMISSION_TYPE_STATUS_LABEL } from 'src/services/manage_suppliers/supplierMapper';
import { ManageSupplier } from 'src/types/manage-suppliers/ManageSupplier';
import * as XLSX from 'xlsx';
export const SUPPLIER_EXPORT_SHEET_NAME = 'Suppliers';

export interface SupplierExportValues {
  companyId: string;
  supplierName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalZip: string;
  contactName: string;
  telecomNumber: string;
  email: string;
  xtrnlSupplierCode: string;
  xtrnlSupplierNumber: string;
  xtrnlSupplierName: string;
  createdBy: string;
  propertyName: string;
  country: string;
  transmissionTypeStatus: string;
  xtrnlSupplierInherited: string;
  accountNumber: string;
  catalog: string;
}

interface SupplierExportColumn {
  title: string;
  resolve: (values: SupplierExportValues) => string;
  minWidth?: number;
}

export const SUPPLIER_EXPORT_COLUMNS: SupplierExportColumn[] = [
  { title: 'COMPANYID', resolve: (values) => values.companyId },
  { title: 'SUPPLIER_NAME', resolve: (values) => values.supplierName },
  { title: 'ADDRESS_LINE_1', resolve: (values) => values.addressLine1 },
  { title: 'ADDRESS_LINE_2', resolve: (values) => values.addressLine2 },
  { title: 'CITY', resolve: (values) => values.city },
  { title: 'STATE', resolve: (values) => values.state },
  { title: 'POSTAL_ZIP', resolve: (values) => values.postalZip },
  { title: 'CONTACT_NAME', resolve: (values) => values.contactName },
  { title: 'TELECOM_NUMBER', resolve: (values) => values.telecomNumber },
  { title: 'NOT_USED', resolve: () => '' },
  { title: 'EMAIL', resolve: (values) => values.email },
  {
    title: 'XTRNL_SUPPLIER_CODE',
    resolve: (values) => values.xtrnlSupplierCode,
    minWidth: 22,
  },
  {
    title: 'XTRNL_SUPPLIER_NUMBER',
    resolve: (values) => values.xtrnlSupplierNumber,
    minWidth: 24,
  },
  {
    title: 'XTRNL_SUPPLIER_NAME',
    resolve: (values) => values.xtrnlSupplierName,
    minWidth: 22,
  },
  { title: 'OWNER_COMPANY', resolve: (values) => values.createdBy ?? '' },
  { title: 'PROPERTY_NAME', resolve: (values) => values.propertyName },
  { title: 'COUNTRY', resolve: (values) => values.country },
  {
    title: 'ORDER_TYPE',
    resolve: (values) => values.transmissionTypeStatus,
    minWidth: 26,
  },
  { title: 'ACCOUNT#', resolve: (values) => values.accountNumber },
  { title: 'CATALOG', resolve: (values) => values.catalog },
  { title: 'XTRNL_SUPPLIER_INHERITED', resolve: (values) => values.xtrnlSupplierInherited },
];

const toExportValue = (value: string | number | null | undefined): string => {
  if (value == null || value === '') {
    return '';
  }

  return String(value);
};

// Export-only: Archived / Inactive rows add the supplier kind from the API's `isOfflineSupplier`.
const toExportSupplierKind = (item: ManageSupplier): string =>
  item.isOfflineSupplier ? 'Offline' : 'Online';

export const toExportTransmissionTypeStatus = (item: ManageSupplier): string => {
  if (item.status === 'archived') {
    return `${TRANSMISSION_TYPE_STATUS_LABEL.archived} (${toExportSupplierKind(item)})`;
  }

  if (item.status === 'inactive') {
    return `${TRANSMISSION_TYPE_STATUS_LABEL.inactive} (${toExportSupplierKind(item)})`;
  }

  if (!item.isOfflineSupplier) {
    if (item.poTransmitMethod === PO_TRANSMIT_INACTIVE) {
      return 'InActive';
    }

    return item.poTransmitMethod === 0 && item.isPunchoutCatalog
      ? TRANSMISSION_TYPE_STATUS_LABEL.onlinePunchout
      : TRANSMISSION_TYPE_STATUS_LABEL.onlineCatalog;
  }

  if (item.isOfflineSupplier) {
    return toExportValue(item.transmissionTypeStatus);
  }

  return '';
};

export const formatManageSupplierForExport = (item: ManageSupplier): SupplierExportValues => ({
  companyId: toExportValue(item.propertyCompanyId),
  supplierName: toExportValue(item.supplierName),
  addressLine1: toExportValue(item.addressLine1),
  addressLine2: toExportValue(item.addressLine2),
  city: toExportValue(item.city),
  state: toExportValue(item.stateProvince),
  postalZip: toExportValue(item.zipPostalCode),
  contactName: toExportValue(item.contactName),
  telecomNumber: toExportValue(item.phone),
  email: toExportValue(item.email),
  xtrnlSupplierCode: toExportValue(item.apId),
  xtrnlSupplierNumber: toExportValue(item.externalSupplierNumber),
  xtrnlSupplierName: toExportValue(item.externalSupplierName),
  createdBy: toExportValue(item.createdBy),
  propertyName: toExportValue(item.propertyName),
  country: toExportValue(item.country),
  transmissionTypeStatus: toExportTransmissionTypeStatus(item),
  xtrnlSupplierInherited: item.xtrnlSupplierInherited === true ? 'Yes' : 'No',
  accountNumber: toExportValue(item.accountNumber),
  catalog: toExportValue(item.catalog),
});

export const getSuppliersExportFileName = (companyName?: string, companyId?: string): string =>
  `${companyName}_Suppliers_${format(new Date(), 'MM_dd_yyyy')}_${companyId}`;

const COLUMN_WIDTH_PADDING = 2;

export const buildSupplierExportColumnWidths = (
  data: string[][],
  columns: SupplierExportColumn[] = SUPPLIER_EXPORT_COLUMNS
): { wch: number }[] =>
  data[0].map((_, columnIndex) => {
    const contentWidth = Math.max(
      ...data.map((row) => (row[columnIndex] ?? '').toString().length),
      0
    );
    const minWidth = columns[columnIndex]?.minWidth ?? 0;

    return { wch: Math.max(contentWidth + COLUMN_WIDTH_PADDING, minWidth) };
  });

export type SupplierExportTranslateFn = (key: string) => string;

export const exportSuppliersToExcel = (
  items: ManageSupplier[],
  companyName?: string,
  companyId?: string,
  t: SupplierExportTranslateFn = (key) => key
): void => {
  const rows = items.map(formatManageSupplierForExport);
  const headerRow = SUPPLIER_EXPORT_COLUMNS.map((column) => t(column.title));
  const data = [
    headerRow,
    ...rows.map((row) => SUPPLIER_EXPORT_COLUMNS.map((column) => column.resolve(row))),
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(data);
  worksheet['!cols'] = buildSupplierExportColumnWidths(data);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, t(SUPPLIER_EXPORT_SHEET_NAME));
  XLSX.writeFile(workbook, `${getSuppliersExportFileName(companyName, companyId)}.xlsx`);
};
